import React from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { getVCFirmWithOrganizations } from '../../services/entityService';
import { CircularProgress, Typography, Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PageContainer from '../../components/layout/PageContainer';
import { MESSAGES } from '../../constants/messages';

const VCFirmDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [vcFirm, setVcFirm] = React.useState<any>(null);
    const [organizations, setOrganizations] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!id) return;
        const fetchVCFirmAndOrganizations = async () => {
            try {
                const { vcFirm, organizations } = await getVCFirmWithOrganizations(id);
                setVcFirm(vcFirm);
                setOrganizations(organizations);
            } catch (err) {
                setError('Failed to fetch VC Firm details');
            } finally {
                setLoading(false);
            }
        };
        fetchVCFirmAndOrganizations();
    }, [id]);

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh"><CircularProgress /></Box>;
    }
    if (error) {
        return <Typography color="error">{error}</Typography>;
    }
    if (!vcFirm) {
        return <Typography>No VC Firm found.</Typography>;
    }

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button 
                    startIcon={<ArrowBack />} 
                    onClick={handleBack}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h4">
                    {vcFirm.name}
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {MESSAGES.PAGE_DISCLAIMER}
            </Typography>
            {vcFirm.description && <Typography><strong>Description:</strong> {vcFirm.description}</Typography>}
            {vcFirm.industries && Array.isArray(vcFirm.industries) && vcFirm.industries.length > 0 && <Typography><strong>Industries:</strong> {vcFirm.industries.join(', ')}</Typography>}
            {vcFirm.investment_theme && Array.isArray(vcFirm.investment_theme) && vcFirm.investment_theme.length > 0 && <Typography><strong>Investment Focus:</strong> {vcFirm.investment_theme.join(", ")}</Typography>}
            {vcFirm.fund_category && <Typography><strong>Fund Category:</strong> {vcFirm.fund_category}</Typography>}
            {vcFirm.websites && vcFirm.websites.length > 0 && (
                <Typography><strong>Websites:</strong> {vcFirm.websites.map((site: string, index: number) => (
                    <span key={index}>
                        <Link href={site} target="_blank" rel="noopener noreferrer">{site}</Link>
                        {index < vcFirm.websites.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {vcFirm.main_hq && <Typography><strong>Main HQ:</strong> {vcFirm.main_hq}</Typography>}
            {vcFirm.locations && vcFirm.locations.length > 0 && <Typography><strong>Locations:</strong> {vcFirm.locations.join(", ")}</Typography>}
            {vcFirm.phone_numbers && vcFirm.phone_numbers.length > 0 && <Typography><strong>Phone Numbers:</strong> {vcFirm.phone_numbers.join(", ")}</Typography>}
            {vcFirm.social_media && vcFirm.social_media.length > 0 && (
                <Typography><strong>Social Media:</strong> {vcFirm.social_media.map((social: string, index: number) => (
                    <span key={index}>
                        <Link href={social} target="_blank" rel="noopener noreferrer">{social}</Link>
                        {index < vcFirm.social_media.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {vcFirm.contact_emails && vcFirm.contact_emails.length > 0 && (
                <Typography><strong>Contact Emails:</strong> {vcFirm.contact_emails.map((email: string, index: number) => (
                    <span key={index}>
                        <Link href={`mailto:${email}`}>{email}</Link>
                        {index < vcFirm.contact_emails.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {Array.isArray(vcFirm.name_alias) && vcFirm.name_alias.length > 0 && <Typography><strong>Also known as:</strong> {vcFirm.name_alias.join(", ")}</Typography>}
            <Box mt={4}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}  >Publicly reported data on portfolio companies and investments with known headquarters and defense affiliations (Israel + global). Partial dataset — additional data and details coming soon</Typography>
                {organizations && organizations.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Industry</TableCell>
                                    <TableCell>Main HQ</TableCell>
                                    <TableCell>Operations in Israel</TableCell>
                                    <TableCell>Unit 8200</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizations.map((organization: any) => (
                                    <TableRow key={organization.id}>
                                        <TableCell>
                                            <Link href={`/organization/${organization.id}`}>{organization.name}</Link>
                                        </TableCell>
                                        <TableCell>
                                            {organization.industries && Array.isArray(organization.industries) && organization.industries.length > 0 && <Typography> {organization.industries.join(', ')}</Typography>}
                                        </TableCell>
                                        <TableCell>{organization.main_hq}</TableCell>
                                        <TableCell>{organization.has_operations_in_israel ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{organization.has_8200_connections ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>{MESSAGES.NO_ORGANIZATIONS_FOUND}</Typography>
                )}
            </Box>

            <Box mt={4}>
                <Typography variant="h6" gutterBottom>Persons of Interest Linked to this VC Firm</Typography>
                {vcFirm.pois && vcFirm.pois.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Position</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Military History</TableCell>
                                    <TableCell>Unit 8200</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vcFirm.pois.map((poi: any) => (
                                    <TableRow key={poi.id}>
                                        <TableCell>
                                            <Link href={`/poi/${poi.id}`}>{poi.name}</Link>
                                        </TableCell>
                                        <TableCell>{poi.position || 'N/A'}</TableCell>
                                        <TableCell>{poi.description}</TableCell>
                                        <TableCell>{poi.history_in_IDF || 'N/A'}</TableCell>
                                        <TableCell>{poi.connection_8200 ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>No persons of interest found.</Typography>
                )}
            </Box>

            {/* VC Investors - VCs that invested in this VC */}
            {vcFirm.vc_investors && vcFirm.vc_investors.length > 0 && (
                <Box mt={4}>
                    <Typography variant="h6" gutterBottom>VC Investors (Backers)</Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Investment Theme</TableCell>
                                    <TableCell>Main HQ</TableCell>
                                    <TableCell>Fund Category</TableCell>
                                    <TableCell>Website</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vcFirm.vc_investors.map((vc: any) => (
                                    <TableRow key={vc.id}>
                                        <TableCell>
                                            <Link component={RouterLink} to={`/vc/${vc.id}`}>
                                                {vc.name}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{vc.investment_theme?.join(', ') || 'N/A'}</TableCell>
                                        <TableCell>{vc.main_hq || 'N/A'}</TableCell>
                                        <TableCell>{vc.fund_category || 'N/A'}</TableCell>
                                        <TableCell>
                                            {vc.websites && vc.websites.length > 0 ? (
                                                <Link href={vc.websites[0]} target="_blank" rel="noopener noreferrer">
                                                    Visit
                                                </Link>
                                            ) : 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* VC Investees - VCs this VC has invested in */}
            {vcFirm.vc_investees && vcFirm.vc_investees.length > 0 && (
                <Box mt={4}>
                    <Typography variant="h6" gutterBottom>VC Portfolio (VC Investments)</Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Investment Theme</TableCell>
                                    <TableCell>Main HQ</TableCell>
                                    <TableCell>Fund Category</TableCell>
                                    <TableCell>Website</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vcFirm.vc_investees.map((vc: any) => (
                                    <TableRow key={vc.id}>
                                        <TableCell>
                                            <Link component={RouterLink} to={`/vc/${vc.id}`}>
                                                {vc.name}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{vc.investment_theme?.join(', ') || 'N/A'}</TableCell>
                                        <TableCell>{vc.main_hq || 'N/A'}</TableCell>
                                        <TableCell>{vc.fund_category || 'N/A'}</TableCell>
                                        <TableCell>
                                            {vc.websites && vc.websites.length > 0 ? (
                                                <Link href={vc.websites[0]} target="_blank" rel="noopener noreferrer">
                                                    Visit
                                                </Link>
                                            ) : 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </PageContainer>
    );
};

export default VCFirmDetail;
